import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Collapse,
  IconButton,
  Chip
} from "@material-ui/core";
import {
  Settings as SettingsIcon,
  Person as PersonIcon,
  Star as StarIcon,
  Schedule as ScheduleIcon,
  Message as MessageIcon,
  Payment as PaymentIcon,
  Security as SecurityIcon,
  VolumeUp as VolumeUpIcon,
  AttachMoney as MoneyIcon,
  DateRange as DateRangeIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CheckCircle as CheckCircleIcon,
  Block as BlockIcon,
  Phone as PhoneIcon,
  ChatBubble as ChatIcon,
  Assignment as AssignmentIcon
} from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";

import { i18n } from "../../translate/i18n";
import useSettings from "../../hooks/useSettings";
import useCompanySettings from "../../hooks/useSettings/companySettings";
import useFormattingSettings from "../../hooks/useFormattingSettings";
import { getCurrencyOptions, getDateFormatOptions } from "../../utils/currencyData";

const useStyles = makeStyles((theme) => ({
  settingContainer: {
    padding: '20px',
    border: '1px solid',
    borderColor: theme.palette.type === 'dark' ? '#444' : '#e0e0e0',
    borderRadius: '8px',
    backgroundColor: theme.palette.type === 'dark' ? '#252525' : '#ffffff',
    transition: 'all 0.2s',
    marginBottom: '16px'
  },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    marginBottom: '8px'
  },
  sectionIcon: {
    marginRight: '8px',
    color: theme.palette.primary.main,
  },
  cardSection: {
    marginBottom: '16px'
  },
  statusChip: {
    marginLeft: '8px'
  }
}));

// Componentes customizados seguindo padrão IA.js
const SettingSwitch = React.memo(({
  id,
  title,
  description,
  value,
  onChange,
  disabled = false,
  icon,
  loading = false
}) => {
  const classes = useStyles();
  const handleChange = useCallback((event) => {
    onChange(id, event.target.checked ? "enabled" : "disabled");
  }, [id, onChange]);

  return (
    <Box className={classes.settingContainer}>
      <Box className={classes.sectionHeader}>
        {icon}
        <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
          {title}
        </Typography>
        <Chip
          icon={value === "enabled" ? <CheckCircleIcon /> : <BlockIcon />}
          label={value === "enabled" ? i18n.t("settings.settings.options.enabled") : i18n.t("settings.settings.options.disabled")}
          color={value === "enabled" ? "primary" : "default"}
          size="small"
          className={classes.statusChip}
        />
      </Box>
      {description && (
        <Typography variant="body2" color="textSecondary" style={{ marginBottom: '16px' }}>
          {description}
        </Typography>
      )}
      <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
        <input
          type="checkbox"
          checked={value === "enabled"}
          onChange={handleChange}
          disabled={disabled || loading}
          style={{ marginRight: '8px' }}
        />
        <Typography variant="body2">
          {loading ? i18n.t("settings.settings.options.updating") : (value === "enabled" ? i18n.t("settings.settings.options.enabled") : i18n.t("settings.settings.options.disabled"))}
        </Typography>
      </label>
    </Box>
  );
});

const SettingSelect = React.memo(({
  id,
  title,
  description,
  value,
  options,
  onChange,
  icon,
  disabled = false,
  loading = false
}) => {
  const classes = useStyles();
  const handleChange = useCallback((event) => {
    onChange(id, event.target.value);
  }, [id, onChange]);

  return (
    <Box className={classes.settingContainer}>
      <Box className={classes.sectionHeader}>
        {icon}
        <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
          {title}
        </Typography>
      </Box>
      {description && (
        <Typography variant="body2" color="textSecondary" style={{ marginBottom: '16px' }}>
          {description}
        </Typography>
      )}
      <select
        value={value}
        onChange={handleChange}
        disabled={disabled || loading}
        style={{
          width: '100%',
          padding: '8px 12px',
          border: '1px solid #e0e0e0',
          borderRadius: '4px',
          fontSize: '14px',
          fontFamily: 'inherit'
        }}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      {loading && (
        <Typography variant="caption" color="textSecondary" style={{ marginTop: '8px' }}>
          {i18n.t("settings.settings.options.updating")}
        </Typography>
      )}
    </Box>
  );
});

const SettingTextField = React.memo(({
  id,
  title,
  description,
  value,
  onChange,
  icon,
  type = "text",
  multiline = false,
  rows = 1,
  disabled = false,
  loading = false,
  secure = false
}) => {
  const classes = useStyles();
  const [localValue, setLocalValue] = useState(value || "");
  const timeoutRef = useRef(null);

  // Atualizar valor local quando prop value mudar
  useEffect(() => {
    setLocalValue(value || "");
  }, [value]);

  const handleChange = useCallback((event) => {
    const newValue = event.target.value;
    setLocalValue(newValue);

    // Limpar timeout anterior
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Salvar após 1 segundo sem digitar
    timeoutRef.current = setTimeout(() => {
      onChange(id, newValue);
    }, 1000);
  }, [id, onChange]);

  // Limpar timeout ao desmontar
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <Box className={classes.settingContainer}>
      <Box className={classes.sectionHeader}>
        {icon}
        <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
          {title}
        </Typography>
        {secure && (
          <Chip
            icon={<SecurityIcon />}
            label={i18n.t("settings.settings.options.secure")}
            color="secondary"
            size="small"
            className={classes.statusChip}
          />
        )}
      </Box>
      {description && (
        <Typography variant="body2" color="textSecondary" style={{ marginBottom: '16px' }}>
          {description}
        </Typography>
      )}
      {multiline ? (
        <textarea
          value={localValue}
          onChange={handleChange}
          disabled={disabled || loading}
          rows={rows}
          placeholder="Digite sua mensagem aqui..."
          style={{
            width: '100%',
            padding: '8px 12px',
            border: '1px solid #e0e0e0',
            borderRadius: '4px',
            fontSize: '14px',
            fontFamily: 'inherit',
            resize: 'vertical'
          }}
        />
      ) : (
        <input
          type={secure ? "password" : type}
          value={localValue}
          onChange={handleChange}
          disabled={disabled || loading}
          style={{
            width: '100%',
            padding: '8px 12px',
            border: '1px solid #e0e0e0',
            borderRadius: '4px',
            fontSize: '14px',
            fontFamily: 'inherit'
          }}
        />
      )}
      {loading && (
        <Typography variant="caption" color="textSecondary" style={{ marginTop: '8px' }}>
          {i18n.t("settings.settings.options.updating")}
        </Typography>
      )}
    </Box>
  );
});

export default function Options(props) {
  const { oldSettings, settings, scheduleTypeChanged, user } = props;
  const classes = useStyles();

  // useRef para evitar re-render infinito
  const isInitializedRef = useRef(false);

  // Estados organizados por seção
  const [generalSettings, setGeneralSettings] = useState({
    userCreation: "disabled",
    userRating: "disabled",
    scheduleType: "disabled",
    chatBotType: "text",
    sendGreetingAccepted: "enabled",
    userRandom: "enabled",
    settingsTransfTicket: "enabled",
    acceptCallWhatsapp: "enabled",
    sendSignMessage: "enabled",
    sendGreetingMessageOneQueues: "enabled",
    sendQueuePosition: "enabled",
    sendFarewellWaitingTicket: "enabled",
    acceptAudioMessageContact: "enabled",
    showNotificationPending: false,
    requiredTag: "enabled",
    closeTicketOnTransfer: false,
    blockTransferOnVacation: true,
    warnTransferOffline: true,
    transferTimeoutMinutes: 0,
    transferTimeoutAction: "pending",
    directTicketsToWallets: false,
    alwaysSelectQueueOnAccept: "disabled",
    copyContactPrefix: ""
  });

  const [paymentSettings, setPaymentSettings] = useState({
    eficlientid: '',
    eficlientsecret: '',
    efichavepix: '',
    mpaccesstoken: '',
    stripeprivatekey: '',
    asaastoken: '',
    openaitoken: ''
  });

  const [lgpdSettings, setLgpdSettings] = useState({
    enableLGPD: "disabled",
    lgpdMessage: "",
    lgpdLink: "",
    lgpdDeleteMessage: "disabled",
    lgpdConsent: "disabled",
    lgpdHideNumber: "disabled"
  });

  const [customMessages, setCustomMessages] = useState({
    transferMessage: "",
    greetingAcceptedMessage: "",
    AcceptCallWhatsappMessage: "",
    sendQueuePositionMessage: ""
  });

  const [expandedSections, setExpandedSections] = useState({
    general: true,
    payment: false,
    lgpd: false,
    system: false,
    messages: false,
    formatting: false,
    ai: false
  });

  const [loading, setLoading] = useState({});

  const { update } = useSettings();
  const { update: updateCompanySettings } = useCompanySettings();
  const {
    currency,
    dateFormat,
    loading: loadingFormattingSettings,
    updateCurrency,
    updateDateFormat
  } = useFormattingSettings();

  // Função para atualizar configurações - memoizada com useCallback
  const handleSettingChange = useCallback(async (key, value) => {
    setLoading(prev => ({ ...prev, [key]: true }));

    try {
      // Lista de configurações que devem ser salvas em CompaniesSettings
      const isCompanySetting = [
        'transferMessage', 
        'greetingAcceptedMessage', 
        'AcceptCallWhatsappMessage', 
        'sendQueuePositionMessage',
        'scheduleType',  // ✅ CORREÇÃO: scheduleType deve ser salvo em CompaniesSettings
        'userRating',
        'sendGreetingAccepted',
        'userRandom',
        'settingsTransfTicket',
        'acceptCallWhatsapp',
        'sendSignMessage',
        'sendGreetingMessageOneQueues',
        'sendQueuePosition',
        'sendFarewellWaitingTicket',
        'acceptAudioMessageContact',
        'chatBotType',
        'enableLGPD',
        'lgpdMessage',
        'lgpdLink',
        'lgpdDeleteMessage',
        'lgpdConsent',
        'lgpdHideNumber',
        'showNotificationPending',
        'closeTicketOnTransfer',
        'blockTransferOnVacation',
        'warnTransferOffline',
        'transferTimeoutMinutes',
        'transferTimeoutAction',
        'directTicketsToWallets',
        'alwaysSelectQueueOnAccept',
        'requiredTag',
        'copyContactPrefix'
      ].includes(key);
      
      if (isCompanySetting) {
        // Usar API de CompaniesSettings
        await updateCompanySettings({ column: key, data: value });
      } else {
        // Usar API de Settings para configurações antigas (userCreation, etc)
        await update({ key, value });
      }

      // Atualizar estado local baseado na seção
      setGeneralSettings(prev => {
        if (Object.keys(prev).includes(key)) {
          return { ...prev, [key]: value };
        }
        return prev;
      });

      setPaymentSettings(prev => {
        if (Object.keys(prev).includes(key)) {
          return { ...prev, [key]: value };
        }
        return prev;
      });

      setLgpdSettings(prev => {
        if (Object.keys(prev).includes(key)) {
          return { ...prev, [key]: value };
        }
        return prev;
      });

      setCustomMessages(prev => {
        if (Object.keys(prev).includes(key)) {
          return { ...prev, [key]: value };
        }
        return prev;
      });

      // Notificar mudança de scheduleType para atualizar aba de Horários
      if (key === "scheduleType" && scheduleTypeChanged) {
        scheduleTypeChanged(value);
      }

    } catch (error) {
      console.error(`Error updating ${key}:`, error);
    } finally {
      setLoading(prev => ({ ...prev, [key]: false }));
    }
  }, [update, updateCompanySettings, scheduleTypeChanged]);

  const handleCurrencyChange = useCallback(async (key, value) => {
    setLoading(prev => ({ ...prev, [key]: true }));
    try {
      await updateCurrency(value);
    } catch (error) {
      console.error(`Error updating ${key}:`, error);
    } finally {
      setLoading(prev => ({ ...prev, [key]: false }));
    }
  }, [updateCurrency]);

  const handleDateFormatChange = useCallback(async (key, value) => {
    setLoading(prev => ({ ...prev, [key]: true }));
    try {
      await updateDateFormat(value);
    } catch (error) {
      console.error(`Error updating ${key}:`, error);
    } finally {
      setLoading(prev => ({ ...prev, [key]: false }));
    }
  }, [updateDateFormat]);

  const toggleSection = useCallback((section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  }, []);

  // Carregar configurações iniciais - CORRIGIDO para evitar loop infinito
  useEffect(() => {
    // Evitar re-processamento se já foi inicializado e oldSettings não mudou
    if (!Array.isArray(oldSettings) || oldSettings.length === 0) {
      return;
    }

    // Prevenir múltiplas inicializações desnecessárias
    if (isInitializedRef.current) {
      return;
    }

    const initialGeneralSettings = {
      userCreation: "disabled",
      userRating: "disabled",
      scheduleType: "disabled",
      chatBotType: "text",
      sendGreetingAccepted: "enabled",
      userRandom: "enabled",
      settingsTransfTicket: "enabled",
      acceptCallWhatsapp: "enabled",
      sendSignMessage: "enabled",
      sendGreetingMessageOneQueues: "enabled",
      sendQueuePosition: "enabled",
      sendFarewellWaitingTicket: "enabled",
      acceptAudioMessageContact: "enabled",
      showNotificationPending: false,
      requiredTag: "enabled",
      closeTicketOnTransfer: false,
      blockTransferOnVacation: true,
      warnTransferOffline: true,
      transferTimeoutMinutes: 0,
      transferTimeoutAction: "pending",
      directTicketsToWallets: false,
      alwaysSelectQueueOnAccept: "disabled",
      copyContactPrefix: ""
    };

    const initialPaymentSettings = {
      eficlientid: '',
      eficlientsecret: '',
      efichavepix: '',
      mpaccesstoken: '',
      stripeprivatekey: '',
      asaastoken: '',
      openaitoken: ''
    };

    const initialLgpdSettings = {
      enableLGPD: "disabled",
      lgpdMessage: "",
      lgpdLink: "",
      lgpdDeleteMessage: "disabled",
      lgpdConsent: "disabled",
      lgpdHideNumber: "disabled"
    };

    const initialCustomMessages = {
      transferMessage: "",
      greetingAcceptedMessage: "",
      AcceptCallWhatsappMessage: "",
      sendQueuePositionMessage: ""
    };

    const newGeneralSettings = { ...initialGeneralSettings };
    const newPaymentSettings = { ...initialPaymentSettings };
    const newLgpdSettings = { ...initialLgpdSettings };
    const newCustomMessages = { ...initialCustomMessages };

    oldSettings.forEach(setting => {
      if (Object.keys(initialGeneralSettings).includes(setting.key)) {
        newGeneralSettings[setting.key] = setting.value;
      } else if (Object.keys(initialPaymentSettings).includes(setting.key)) {
        newPaymentSettings[setting.key] = setting.value;
      } else if (Object.keys(initialLgpdSettings).includes(setting.key)) {
        newLgpdSettings[setting.key] = setting.value;
      }
    });

    // Carregar mensagens personalizadas de settings (CompaniesSettings)
    if (settings) {
      Object.keys(initialCustomMessages).forEach(key => {
        if (settings[key] !== undefined) {
          newCustomMessages[key] = settings[key];
        }
      });

      // ✅ CORREÇÃO: Carregar configurações que estão em CompaniesSettings
      const companiesSettingsKeys = [
        'scheduleType',
        'userRating',
        'sendGreetingAccepted',
        'userRandom',
        'settingsTransfTicket',
        'acceptCallWhatsapp',
        'sendSignMessage',
        'sendGreetingMessageOneQueues',
        'sendQueuePosition',
        'sendFarewellWaitingTicket',
        'acceptAudioMessageContact',
        'chatBotType',
        'enableLGPD',
        'lgpdMessage',
        'lgpdLink',
        'lgpdDeleteMessage',
        'lgpdConsent',
        'lgpdHideNumber'
      ];

      companiesSettingsKeys.forEach(key => {
        if (settings[key] !== undefined) {
          if (Object.keys(initialGeneralSettings).includes(key)) {
            newGeneralSettings[key] = settings[key];
          } else if (Object.keys(initialLgpdSettings).includes(key)) {
            newLgpdSettings[key] = settings[key];
          }
        }
      });
    }

    setGeneralSettings(newGeneralSettings);
    setPaymentSettings(newPaymentSettings);
    setLgpdSettings(newLgpdSettings);
    setCustomMessages(newCustomMessages);

    // Marcar como inicializado
    isInitializedRef.current = true;
  }, [oldSettings, settings]);

  const isSuper = () => user?.super;

  const scheduleOptions = [
    { value: "disabled", label: i18n.t("settings.settings.options.disabled") },
    { value: "queue", label: i18n.t("settings.settings.options.queueManagement") },
    { value: "company", label: i18n.t("settings.settings.options.companyManagement") },
    { value: "connection", label: i18n.t("settings.settings.options.connectionManagement") }
  ];

  const chatBotOptions = [
    { value: "text", label: i18n.t("settings.settings.options.textOnly") },
    { value: "voice", label: i18n.t("settings.settings.options.voiceAndText") }
  ];

  return (
    <Box>
      {/* Header */}
      <Box className={classes.settingContainer}>
        <Box className={classes.sectionHeader}>
          <SettingsIcon className={classes.sectionIcon} />
          <Typography variant="h6" style={{ fontWeight: 600 }}>
            {i18n.t("settings.settings.options.title")}
          </Typography>
        </Box>
        <Typography variant="body2" color="textSecondary" style={{ marginBottom: '16px' }}>
          {i18n.t("settings.settings.options.description")}
        </Typography>
      </Box>

      {/* Configurações Gerais */}
      <Card className={classes.cardSection}>
        <CardHeader
          avatar={<SettingsIcon />}
          title={i18n.t("settings.settings.options.generalSettings")}
          action={
            <IconButton onClick={() => toggleSection('general')}>
              {expandedSections.general ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          }
        />
        <Collapse in={expandedSections.general}>
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <SettingSwitch
                  id="userCreation"
                  title={i18n.t("settings.settings.options.creationCompanyUser")}
                  description={i18n.t("settings.settings.options.creationCompanyUserDesc")}
                  value={generalSettings.userCreation}
                  onChange={handleSettingChange}
                  icon={<PersonIcon className={classes.sectionIcon} />}
                  loading={loading.userCreation}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <SettingSwitch
                  id="userRating"
                  title={i18n.t("settings.settings.options.evaluations")}
                  description={i18n.t("settings.settings.options.evaluationsDesc")}
                  value={generalSettings.userRating}
                  onChange={handleSettingChange}
                  icon={<StarIcon className={classes.sectionIcon} />}
                  loading={loading.userRating}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <SettingSelect
                  id="scheduleType"
                  title={i18n.t("settings.settings.options.officeScheduling")}
                  description={i18n.t("settings.settings.options.officeSchedulingDesc")}
                  value={generalSettings.scheduleType}
                  options={scheduleOptions}
                  onChange={handleSettingChange}
                  icon={<ScheduleIcon className={classes.sectionIcon} />}
                  loading={loading.scheduleType}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <SettingSwitch
                  id="sendGreetingAccepted"
                  title={i18n.t("settings.settings.options.sendGreetingAccepted")}
                  description={i18n.t("settings.settings.options.sendGreetingAcceptedDesc")}
                  value={generalSettings.sendGreetingAccepted}
                  onChange={handleSettingChange}
                  icon={<MessageIcon className={classes.sectionIcon} />}
                  loading={loading.sendGreetingAccepted}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <SettingSwitch
                  id="userRandom"
                  title={i18n.t("settings.settings.options.userRandom")}
                  description={i18n.t("settings.settings.options.userRandomDesc")}
                  value={generalSettings.userRandom}
                  onChange={handleSettingChange}
                  icon={<PersonIcon className={classes.sectionIcon} />}
                  loading={loading.userRandom}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <SettingSwitch
                  id="acceptCallWhatsapp"
                  title={i18n.t("settings.settings.options.acceptCallWhatsapp")}
                  description={i18n.t("settings.settings.options.acceptCallWhatsappDesc")}
                  value={generalSettings.acceptCallWhatsapp}
                  onChange={handleSettingChange}
                  icon={<PhoneIcon className={classes.sectionIcon} />}
                  loading={loading.acceptCallWhatsapp}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <SettingSwitch
                  id="sendSignMessage"
                  title={i18n.t("settings.settings.options.sendSignMessage")}
                  description={i18n.t("settings.settings.options.sendSignMessageDesc")}
                  value={generalSettings.sendSignMessage}
                  onChange={handleSettingChange}
                  icon={<MessageIcon className={classes.sectionIcon} />}
                  loading={loading.sendSignMessage}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <SettingSwitch
                  id="sendGreetingMessageOneQueues"
                  title={i18n.t("settings.settings.options.sendGreetingMessageOneQueues")}
                  description={i18n.t("settings.settings.options.sendGreetingMessageOneQueuesDesc")}
                  value={generalSettings.sendGreetingMessageOneQueues}
                  onChange={handleSettingChange}
                  icon={<MessageIcon className={classes.sectionIcon} />}
                  loading={loading.sendGreetingMessageOneQueues}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <SettingSwitch
                  id="sendQueuePosition"
                  title={i18n.t("settings.settings.options.sendQueuePosition")}
                  description={i18n.t("settings.settings.options.sendQueuePositionDesc")}
                  value={generalSettings.sendQueuePosition}
                  onChange={handleSettingChange}
                  icon={<AssignmentIcon className={classes.sectionIcon} />}
                  loading={loading.sendQueuePosition}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <SettingSwitch
                  id="sendFarewellWaitingTicket"
                  title={i18n.t("settings.settings.options.sendFarewellWaitingTicket")}
                  description={i18n.t("settings.settings.options.sendFarewellWaitingTicketDesc")}
                  value={generalSettings.sendFarewellWaitingTicket}
                  onChange={handleSettingChange}
                  icon={<MessageIcon className={classes.sectionIcon} />}
                  loading={loading.sendFarewellWaitingTicket}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <SettingSwitch
                  id="acceptAudioMessageContact"
                  title={i18n.t("settings.settings.options.acceptAudioMessageContact")}
                  description={i18n.t("settings.settings.options.acceptAudioMessageContactDesc")}
                  value={generalSettings.acceptAudioMessageContact}
                  onChange={handleSettingChange}
                  icon={<VolumeUpIcon className={classes.sectionIcon} />}
                  loading={loading.acceptAudioMessageContact}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <SettingSwitch
                  id="settingsTransfTicket"
                  title={i18n.t("settings.settings.options.settingsTransfTicket")}
                  description={i18n.t("settings.settings.options.settingsTransfTicketDesc")}
                  value={generalSettings.settingsTransfTicket}
                  onChange={handleSettingChange}
                  icon={<AssignmentIcon className={classes.sectionIcon} />}
                  loading={loading.settingsTransfTicket}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <SettingSelect
                  id="chatBotType"
                  title={i18n.t("settings.settings.options.chatBotType")}
                  description={i18n.t("settings.settings.options.chatBotTypeDesc")}
                  value={generalSettings.chatBotType}
                  options={chatBotOptions}
                  onChange={handleSettingChange}
                  icon={<ChatIcon className={classes.sectionIcon} />}
                  loading={loading.chatBotType}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Collapse>
      </Card>

      {/* Configurações de Pagamento */}
      {isSuper() && (
        <Card className={classes.cardSection}>
          <CardHeader
            avatar={<PaymentIcon />}
            title={i18n.t("settings.settings.options.paymentSettings")}
            action={
              <IconButton onClick={() => toggleSection('payment')}>
                {expandedSections.payment ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            }
          />
          <Collapse in={expandedSections.payment}>
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <SettingTextField
                    id="eficlientid"
                    title={i18n.t("settings.settings.options.efiClientId")}
                    description={i18n.t("settings.settings.options.efiClientIdDesc")}
                    value={paymentSettings.eficlientid}
                    onChange={handleSettingChange}
                    icon={<PaymentIcon className={classes.sectionIcon} />}
                    loading={loading.eficlientid}
                    secure
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <SettingTextField
                    id="eficlientsecret"
                    title={i18n.t("settings.settings.options.efiClientSecret")}
                    description={i18n.t("settings.settings.options.efiClientSecretDesc")}
                    value={paymentSettings.eficlientsecret}
                    onChange={handleSettingChange}
                    icon={<SecurityIcon className={classes.sectionIcon} />}
                    loading={loading.eficlientsecret}
                    secure
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <SettingTextField
                    id="mpaccesstoken"
                    title={i18n.t("settings.settings.options.mpAccessToken")}
                    description={i18n.t("settings.settings.options.mpAccessTokenDesc")}
                    value={paymentSettings.mpaccesstoken}
                    onChange={handleSettingChange}
                    icon={<MoneyIcon className={classes.sectionIcon} />}
                    loading={loading.mpaccesstoken}
                    secure
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <SettingTextField
                    id="asaastoken"
                    title={i18n.t("settings.settings.options.asaasToken")}
                    description={i18n.t("settings.settings.options.asaasTokenDesc")}
                    value={paymentSettings.asaastoken}
                    onChange={handleSettingChange}
                    icon={<PaymentIcon className={classes.sectionIcon} />}
                    loading={loading.asaastoken}
                    secure
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <SettingTextField
                    id="efichavepix"
                    title={i18n.t("settings.settings.options.efiChavePix")}
                    description={i18n.t("settings.settings.options.efiChavePixDesc")}
                    value={paymentSettings.efichavepix}
                    onChange={handleSettingChange}
                    icon={<PaymentIcon className={classes.sectionIcon} />}
                    loading={loading.efichavepix}
                    secure
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <SettingTextField
                    id="stripeprivatekey"
                    title={i18n.t("settings.settings.options.stripePrivateKey")}
                    description={i18n.t("settings.settings.options.stripePrivateKeyDesc")}
                    value={paymentSettings.stripeprivatekey}
                    onChange={handleSettingChange}
                    icon={<PaymentIcon className={classes.sectionIcon} />}
                    loading={loading.stripeprivatekey}
                    secure
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <SettingTextField
                    id="openaitoken"
                    title={i18n.t("settings.settings.options.openAiToken")}
                    description={i18n.t("settings.settings.options.openAiTokenDesc")}
                    value={paymentSettings.openaitoken}
                    onChange={handleSettingChange}
                    icon={<SecurityIcon className={classes.sectionIcon} />}
                    loading={loading.openaitoken}
                    secure
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Collapse>
        </Card>
      )}

      {/* Configurações de LGPD */}
      <Card className={classes.cardSection}>
        <CardHeader
          avatar={<SecurityIcon />}
          title={i18n.t("settings.settings.options.lgpdSettings")}
          action={
            <IconButton onClick={() => toggleSection('lgpd')}>
              {expandedSections.lgpd ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          }
        />
        <Collapse in={expandedSections.lgpd}>
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <SettingSwitch
                  id="enableLGPD"
                  title={i18n.t("settings.settings.options.enableLGPD")}
                  description={i18n.t("settings.settings.options.enableLGPDDesc")}
                  value={lgpdSettings.enableLGPD}
                  onChange={handleSettingChange}
                  icon={<SecurityIcon className={classes.sectionIcon} />}
                  loading={loading.enableLGPD}
                />
              </Grid>

              {lgpdSettings.enableLGPD === "enabled" && (
                <>
                  <Grid item xs={12}>
                    <SettingTextField
                      id="lgpdMessage"
                      title={i18n.t("settings.settings.options.lgpdMessage")}
                      description={i18n.t("settings.settings.options.lgpdMessageDesc")}
                      value={lgpdSettings.lgpdMessage}
                      onChange={handleSettingChange}
                      icon={<MessageIcon className={classes.sectionIcon} />}
                      loading={loading.lgpdMessage}
                      multiline
                      rows={4}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <SettingTextField
                      id="lgpdLink"
                      title={i18n.t("settings.settings.options.lgpdLink")}
                      description={i18n.t("settings.settings.options.lgpdLinkDesc")}
                      value={lgpdSettings.lgpdLink}
                      onChange={handleSettingChange}
                      icon={<SecurityIcon className={classes.sectionIcon} />}
                      loading={loading.lgpdLink}
                      type="url"
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <SettingSwitch
                      id="lgpdDeleteMessage"
                      title={i18n.t("settings.settings.options.lgpdDeleteMessage")}
                      description={i18n.t("settings.settings.options.lgpdDeleteMessageDesc")}
                      value={lgpdSettings.lgpdDeleteMessage}
                      onChange={handleSettingChange}
                      icon={<SecurityIcon className={classes.sectionIcon} />}
                      loading={loading.lgpdDeleteMessage}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <SettingSwitch
                      id="lgpdConsent"
                      title={i18n.t("settings.settings.options.lgpdConsent")}
                      description={i18n.t("settings.settings.options.lgpdConsentDesc")}
                      value={lgpdSettings.lgpdConsent}
                      onChange={handleSettingChange}
                      icon={<SecurityIcon className={classes.sectionIcon} />}
                      loading={loading.lgpdConsent}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <SettingSwitch
                      id="lgpdHideNumber"
                      title={i18n.t("settings.settings.options.lgpdHideNumber")}
                      description={i18n.t("settings.settings.options.lgpdHideNumberDesc")}
                      value={lgpdSettings.lgpdHideNumber}
                      onChange={handleSettingChange}
                      icon={<SecurityIcon className={classes.sectionIcon} />}
                      loading={loading.lgpdHideNumber}
                    />
                  </Grid>
                </>
              )}
            </Grid>
          </CardContent>
        </Collapse>
      </Card>

      {/* Configurações de Formatação */}
      <Card className={classes.cardSection}>
        <CardHeader
          avatar={<DateRangeIcon />}
          title={i18n.t("settings.settings.options.formattingSettings")}
          action={
            <IconButton onClick={() => toggleSection('formatting')}>
              {expandedSections.formatting ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          }
        />
        <Collapse in={expandedSections.formatting}>
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <SettingSelect
                  id="systemCurrency"
                  title={i18n.t("settings.settings.options.systemCurrency")}
                  description={i18n.t("settings.settings.options.systemCurrencyDesc")}
                  value={currency}
                  options={getCurrencyOptions()}
                  onChange={handleCurrencyChange}
                  icon={<MoneyIcon className={classes.sectionIcon} />}
                  loading={loadingFormattingSettings}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <SettingSelect
                  id="systemDateFormat"
                  title={i18n.t("settings.settings.options.systemDateFormat")}
                  description={i18n.t("settings.settings.options.systemDateFormatDesc")}
                  value={dateFormat}
                  options={getDateFormatOptions()}
                  onChange={handleDateFormatChange}
                  icon={<DateRangeIcon className={classes.sectionIcon} />}
                  loading={loadingFormattingSettings}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Collapse>
      </Card>

      {/* Configurações de Sistema */}
      <Card className={classes.cardSection}>
        <CardHeader
          avatar={<SettingsIcon />}
          title={i18n.t("settings.settings.options.systemSettings")}
          action={
            <IconButton onClick={() => toggleSection('system')}>
              {expandedSections.system ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          }
        />
        <Collapse in={expandedSections.system}>
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <SettingSwitch
                  id="requiredTag"
                  title={i18n.t("settings.settings.options.requiredTag")}
                  description={i18n.t("settings.settings.options.requiredTagDesc")}
                  value={generalSettings.requiredTag}
                  onChange={handleSettingChange}
                  icon={<AssignmentIcon className={classes.sectionIcon} />}
                  loading={loading.requiredTag}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <SettingSwitch
                  id="closeTicketOnTransfer"
                  title={i18n.t("settings.settings.options.closeTicketOnTransfer")}
                  description={i18n.t("settings.settings.options.closeTicketOnTransferDesc")}
                  value={generalSettings.closeTicketOnTransfer ? "enabled" : "disabled"}
                  onChange={(key, value) => handleSettingChange(key, value === "enabled")}
                  icon={<AssignmentIcon className={classes.sectionIcon} />}
                  loading={loading.closeTicketOnTransfer}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <SettingSwitch
                  id="blockTransferOnVacation"
                  title="Bloquear Transferência para Atendentes em Férias/Licença"
                  description="Impede transferências diretas para atendentes ausentes, de férias ou com licença programada."
                  value={generalSettings.blockTransferOnVacation !== false ? "enabled" : "disabled"}
                  onChange={(key, value) => handleSettingChange(key, value === "enabled")}
                  icon={<BlockIcon className={classes.sectionIcon} />}
                  loading={loading.blockTransferOnVacation}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <SettingSwitch
                  id="warnTransferOffline"
                  title="Avisar ao Transferir para Atendente Offline"
                  description="Exibe aviso de alerta no modal de transferência se o atendente selecionado estiver desconectado."
                  value={generalSettings.warnTransferOffline !== false ? "enabled" : "disabled"}
                  onChange={(key, value) => handleSettingChange(key, value === "enabled")}
                  icon={<PersonIcon className={classes.sectionIcon} />}
                  loading={loading.warnTransferOffline}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <SettingTextField
                  id="transferTimeoutMinutes"
                  title="Timeout de Transferência (Minutos)"
                  description="Tempo limite para o atendente responder ao ticket transferido. Se não responder em X minutos, o ticket volta automaticamente para a fila (0 = desativado)."
                  value={String(generalSettings.transferTimeoutMinutes || 0)}
                  onChange={(key, value) => handleSettingChange(key, parseInt(value || "0", 10))}
                  icon={<ScheduleIcon className={classes.sectionIcon} />}
                  loading={loading.transferTimeoutMinutes}
                  type="number"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <SettingSwitch
                  id="directTicketsToWallets"
                  title={i18n.t("settings.settings.options.directTicketsToWallets")}
                  description={i18n.t("settings.settings.options.directTicketsToWalletsDesc")}
                  value={generalSettings.directTicketsToWallets ? "enabled" : "disabled"}
                  onChange={(key, value) => handleSettingChange(key, value === "enabled")}
                  icon={<PersonIcon className={classes.sectionIcon} />}
                  loading={loading.directTicketsToWallets}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <SettingSwitch
                  id="showNotificationPending"
                  title={i18n.t("settings.settings.options.showNotificationPending")}
                  description={i18n.t("settings.settings.options.showNotificationPendingDesc")}
                  value={generalSettings.showNotificationPending ? "enabled" : "disabled"}
                  onChange={(key, value) => handleSettingChange(key, value === "enabled")}
                  icon={<MessageIcon className={classes.sectionIcon} />}
                  loading={loading.showNotificationPending}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <SettingSwitch
                  id="alwaysSelectQueueOnAccept"
                  title={i18n.t("settings.settings.options.alwaysSelectQueueOnAccept")}
                  description={i18n.t("settings.settings.options.alwaysSelectQueueOnAcceptDesc")}
                  value={generalSettings.alwaysSelectQueueOnAccept}
                  onChange={handleSettingChange}
                  icon={<AssignmentIcon className={classes.sectionIcon} />}
                  loading={loading.alwaysSelectQueueOnAccept}
                />
              </Grid>

              <Grid item xs={12}>
                <SettingTextField
                  id="copyContactPrefix"
                  title={i18n.t("settings.settings.options.copyContactPrefix")}
                  description={i18n.t("settings.settings.options.copyContactPrefixDesc")}
                  value={generalSettings.copyContactPrefix}
                  onChange={handleSettingChange}
                  icon={<PersonIcon className={classes.sectionIcon} />}
                  loading={loading.copyContactPrefix}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Collapse>
      </Card>

      {/* Mensagens Customizadas */}
      <Card className={classes.cardSection}>
        <CardHeader
          avatar={<ChatIcon />}
          title={i18n.t("settings.settings.options.customMessages")}
          action={
            <IconButton onClick={() => toggleSection('messages')}>
              {expandedSections.messages ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          }
        />
        <Collapse in={expandedSections.messages}>
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <SettingTextField
                  id="transferMessage"
                  title={i18n.t("settings.settings.options.transferMessage")}
                  description={i18n.t("settings.settings.options.transferMessageDesc")}
                  value={customMessages.transferMessage}
                  onChange={handleSettingChange}
                  icon={<AssignmentIcon className={classes.sectionIcon} />}
                  loading={loading.transferMessage}
                  multiline
                  rows={3}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <SettingTextField
                  id="greetingAcceptedMessage"
                  title={i18n.t("settings.settings.options.greetingAcceptedMessage")}
                  description={i18n.t("settings.settings.options.greetingAcceptedMessageDesc")}
                  value={customMessages.greetingAcceptedMessage}
                  onChange={handleSettingChange}
                  icon={<MessageIcon className={classes.sectionIcon} />}
                  loading={loading.greetingAcceptedMessage}
                  multiline
                  rows={3}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <SettingTextField
                  id="AcceptCallWhatsappMessage"
                  title={i18n.t("settings.settings.options.acceptCallWhatsappMessage")}
                  description={i18n.t("settings.settings.options.acceptCallWhatsappMessageDesc")}
                  value={customMessages.AcceptCallWhatsappMessage}
                  onChange={handleSettingChange}
                  icon={<PhoneIcon className={classes.sectionIcon} />}
                  loading={loading.AcceptCallWhatsappMessage}
                  multiline
                  rows={3}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <SettingTextField
                  id="sendQueuePositionMessage"
                  title={i18n.t("settings.settings.options.sendQueuePositionMessage")}
                  description={i18n.t("settings.settings.options.sendQueuePositionMessageDesc")}
                  value={customMessages.sendQueuePositionMessage}
                  onChange={handleSettingChange}
                  icon={<AssignmentIcon className={classes.sectionIcon} />}
                  loading={loading.sendQueuePositionMessage}
                  multiline
                  rows={3}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Collapse>
      </Card>
    </Box>
  );
}
